"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.prepareProjectParticipantsAntdTableData = exports.useProjectParticipantsHook = void 0;
const react_1 = require("react");
const use_custom_compare_1 = require("use-custom-compare");
const lodash_1 = __importDefault(require("lodash"));
const ICCanisterUtils_1 = require("src/hello_assets/src/util/ICCanisterUtils");
const typescriptAddons_1 = require("src/hello_assets/src/util/typescriptAddons");
const DateTimeFacade_1 = require("src/hello_assets/src/util/DateTimeFacade");
const ActorsProvider_1 = require("src/hello_assets/src/components/system/actor/ActorsProvider");
function useProjectParticipantsHook() {
    const actorsContext = (0, ActorsProvider_1.useActorsContext)();
    const [state, setState] = (0, react_1.useReducer)((state, newState) => ({ ...state, ...newState }), {
        inProgress: false,
        loaded: false,
        data: [],
        error: undefined,
        isError: false
    });
    const proceedWithFetchingAllParticipants = async (projectApiKey, managementActor) => {
        const getProjectParticipantsResult = await managementActor.getProjectParticipants(projectApiKey);
        if ((0, ICCanisterUtils_1.isICRequestOk)(getProjectParticipantsResult)) {
            const participantViews = getProjectParticipantsResult.ok;
            setState({
                inProgress: false,
                loaded: true,
                data: participantViews,
                error: undefined,
                isError: false
            });
            return true;
        }
        else if ((0, ICCanisterUtils_1.isICRequestErr)(getProjectParticipantsResult)) {
            const errorName = (0, ICCanisterUtils_1.getICRequestErrName)(getProjectParticipantsResult);
            throw new ICCanisterUtils_1.ICRestError(errorName);
        }
        return false;
    };
    const getProjectParticipants = (0, react_1.useCallback)(async (projectApiKey) => {
        setState({ inProgress: true });
        try {
            const managementActor = await actorsContext.getManagementActor();
            if (managementActor) {
                const success = await proceedWithFetchingAllParticipants(projectApiKey, managementActor);
                if (success) {
                    return;
                }
            }
            throw new ICCanisterUtils_1.ICRestError("unknown error");
        }
        catch (e) {
            const error = e instanceof ICCanisterUtils_1.ICRestError ? e : ICCanisterUtils_1.ICRestError.withNativeError(e);
            console.warn("useProjectParticipantsHook.getProjectParticipants: failed with error", error);
            setState({
                inProgress: false,
                loaded: true,
                error: error.toNativeError(),
                isError: true
            });
        }
    }, [actorsContext.getManagementActor]);
    const addNewViewerParticipant = (0, react_1.useCallback)(async (projectApiKey, participantPrincipal, participantName) => {
        const managementActor = await actorsContext.getManagementActor();
        if (managementActor) {
            const addNewViewerParticipantResult = await managementActor.addNewViewerParticipant(projectApiKey, participantPrincipal.toText(), participantName);
            if ((0, ICCanisterUtils_1.isICRequestOk)(addNewViewerParticipantResult)) {
                //fetch all
                const success = await proceedWithFetchingAllParticipants(projectApiKey, managementActor);
                if (success) {
                    return;
                }
            }
            else if ((0, ICCanisterUtils_1.isICRequestErr)(addNewViewerParticipantResult)) {
                const errorName = (0, ICCanisterUtils_1.getICRequestErrName)(addNewViewerParticipantResult);
                throw new ICCanisterUtils_1.ICRestError(errorName);
            }
        }
        throw new ICCanisterUtils_1.ICRestError("unknown error");
    }, [actorsContext.getManagementActor]);
    const removeParticipant = (0, react_1.useCallback)(async (projectApiKey, participantPrincipal) => {
        const managementActor = await actorsContext.getManagementActor();
        if (managementActor) {
            const removeParticipantResult = await managementActor.removeParticipant(projectApiKey, participantPrincipal.toText());
            if ((0, ICCanisterUtils_1.isICRequestOk)(removeParticipantResult)) {
                //fetch all
                const success = await proceedWithFetchingAllParticipants(projectApiKey, managementActor);
                if (success) {
                    return;
                }
            }
            else if ((0, ICCanisterUtils_1.isICRequestErr)(removeParticipantResult)) {
                const errorName = (0, ICCanisterUtils_1.getICRequestErrName)(removeParticipantResult);
                throw new ICCanisterUtils_1.ICRestError(errorName);
            }
        }
        throw new ICCanisterUtils_1.ICRestError("unknown error");
    }, [actorsContext.getManagementActor]);
    const updateParticipantName = (0, react_1.useCallback)(async (projectApiKey, participantPrincipal, newParticipantName) => {
        const managementActor = await actorsContext.getManagementActor();
        if (managementActor) {
            const updateParticipantNameResult = await managementActor.updateParticipantName(projectApiKey, participantPrincipal.toText(), newParticipantName);
            if ((0, ICCanisterUtils_1.isICRequestOk)(updateParticipantNameResult)) {
                //fetch all
                const success = await proceedWithFetchingAllParticipants(projectApiKey, managementActor);
                if (success) {
                    return;
                }
            }
            else if ((0, ICCanisterUtils_1.isICRequestErr)(updateParticipantNameResult)) {
                const errorName = (0, ICCanisterUtils_1.getICRequestErrName)(updateParticipantNameResult);
                throw new ICCanisterUtils_1.ICRestError(errorName);
            }
        }
        throw new ICCanisterUtils_1.ICRestError("unknown error");
    }, [actorsContext.getManagementActor]);
    const management = (0, react_1.useMemo)(() => ({
        getProjectParticipants: getProjectParticipants,
        addNewViewerParticipant: addNewViewerParticipant,
        removeParticipant: removeParticipant,
        updateParticipantName: updateParticipantName,
    }), []);
    return (0, use_custom_compare_1.useCustomCompareMemo)(() => ({
        state, management
    }), [state, management], (prevDeps, nextDeps) => {
        const prevState = prevDeps[0];
        const nextState = nextDeps[0];
        return lodash_1.default.isEqual(prevState, nextState);
    });
}
exports.useProjectParticipantsHook = useProjectParticipantsHook;
const getRoleName = (role) => {
    if ((0, typescriptAddons_1.hasOwnProperty)(role, "owner")) {
        return "Owner";
    }
    else if ((0, typescriptAddons_1.hasOwnProperty)(role, "viewer")) {
        return "Viewer";
    }
    else if ((0, typescriptAddons_1.hasOwnProperty)(role, "admin")) {
        return "Admin";
    }
    return "Unknown";
};
const prepareProjectParticipantsAntdTableData = (state) => {
    return (state.data || []).map(item => {
        const { name, principal, role, createdTimeSec } = item;
        const createdTimeString = DateTimeFacade_1.DateTimeFacade.getMomentFromMillis(createdTimeSec * 1000).format(DateTimeFacade_1.DateTimeFormatByFunctionality.HumanReadable.Project.Members.CreatedDate);
        const itemResult = {
            key: principal.toText(),
            name: name,
            principal: principal.toText(),
            role: getRoleName(role),
            date: createdTimeString,
            permissions: {
                isCurrentUser: (0, typescriptAddons_1.hasOwnProperty)(role, "owner"),
                canBeDeleted: (0, typescriptAddons_1.hasOwnProperty)(role, "viewer"),
                canBeEdited: (0, typescriptAddons_1.hasOwnProperty)(role, "viewer"),
            },
            item
        };
        return itemResult;
    });
};
exports.prepareProjectParticipantsAntdTableData = prepareProjectParticipantsAntdTableData;
